import React, { useEffect, useState } from 'react';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { Loader } from 'components/atoms/Loader';
import { Grid } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { Alert, EAlertVariants } from 'components/atoms/Alert';
import { useNavigate, useParams } from 'react-router-dom';
import { useConfigurationLicence } from '../hooks';
import { DetailLicenceTemplatePage } from '../DetailLicenceTemplatePage';
import { EDictionaryTypes } from 'models/dictionary';
import { findInDictionary } from 'helpers/dictionary';
import { EApplicationProcessStatus, EWeaponLicenceTemplateStatusId } from '../helper';
import { FormModal } from 'components/molecules/Modal';
import { useAlert } from 'models/alertContext';
import { clearAlertTimeout, EAlertTime } from 'utils/date';
import { ActionTypes } from 'state/actions/alert';
import { useSystemFieldsConfiguration } from 'pages/SystemSettingsPage/SystemFieldsConfigurationPage/hooks';
import { ILicenceTemplateResponse } from 'models/licenceConfigurator';

const LicenceTemplatePage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { clearAlert, samePageAlert, nextPageAlert, setAlert } = useAlert();
  const [isProcessingPricingLoading, setIsProcessingPricingLoading] = useState(true);
  const [publishIsDisabled, setPublishIsDisabled] = useState(true);
  const [publishStatusLoading, setPublishStatusLoading] = useState(true);
  const [status, setStatus] = useState<string>('');
  const [isActiveStatus, setIsActiveStatus] = useState<boolean>();
  const [isOpenModalActivate, setIsOpenModalActivate] = useState<boolean>(false);
  const [isOpenModalDeactivate, setIsOpenModalDeactivate] = useState<boolean>(false);
  const { getFieldsOptional, requiredFieldsName } = useSystemFieldsConfiguration();
  const [initLicenceValues, setInitLicenceValues] = useState<ILicenceTemplateResponse>();

  useEffect(() => {
    getFieldsOptional();
  }, []);

  useEffect(
    () => () => {
      clearAlert(ActionTypes.CLEAR_NEXT_PAGE_ALERT);
      clearAlert(ActionTypes.CLEAR_SAME_PAGE_ALERT);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(
    () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_SAME_PAGE_ALERT, EAlertTime.LONG);
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_NEXT_PAGE_ALERT, EAlertTime.LONG);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const {
    getLicence,
    licence,
    handleChangeStatus,
    getLicenceApplicationProccessData,
    licencePrices,
    weaponLicenceTemplateStatus,
    loadDictionaries,
  } = useConfigurationLicence();

  useEffect(() => {
    loadDictionaries([EDictionaryTypes.weaponLicenceTemplateStatus]);
  }, []);

  useEffect(() => {
    (async function getDataInit() {
      setIsLoading(true);
      if (id) {
        getLicence(id);
      }
      setIsLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (licence && weaponLicenceTemplateStatus) {
      setStatus(findInDictionary(licence.statusUniqueId, weaponLicenceTemplateStatus) ?? '');
      setIsActiveStatus(licence.statusUniqueId === EWeaponLicenceTemplateStatusId.active);
    }
  }, [licence, weaponLicenceTemplateStatus]);

  useEffect(() => {
    if (licence) {
      const initData = licence;
      if (requiredFieldsName.length) {
        type Individual = typeof licence.owner.individual;
        const individual: Individual = { ...licence.owner.individual };
        requiredFieldsName.forEach((field) => {
          if (field in individual) {
            (individual[field as keyof Individual] as boolean) = true;
          }
        });

        type Organisation = typeof licence.owner.organisation;
        const organisation: Organisation = { ...licence.owner.organisation };
        requiredFieldsName.forEach((field) => {
          if (field in organisation) {
            (organisation[field as keyof Organisation] as boolean) = true;
          }
        });
        initData.owner.individual = individual;
        initData.owner.organisation = organisation;
      }
      setInitLicenceValues(initData);
    }
  }, [licence, requiredFieldsName]);

  useEffect(() => {
    (async function init() {
      if (licence) {
        setIsProcessingPricingLoading(true);
        await getLicenceApplicationProccessData(licence.uniqueId);
        setIsProcessingPricingLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licence]);

  useEffect(() => {
    if (!isProcessingPricingLoading) {
      setPublishStatusLoading(true);
      if (
        licencePrices.length &&
        licencePrices.filter(
          (item) => item.statusUniqueId === EApplicationProcessStatus.active,
        ).length
      ) {
        setPublishIsDisabled(false);
      } else {
        setPublishIsDisabled(true);
      }
      setPublishStatusLoading(false);
    }
  }, [licencePrices]);

  const changeStatus = async () => {
    if (id) {
      const res = await handleChangeStatus(id, !!isActiveStatus);
      if (res) {
        await getLicence(id);
      }
    }
    setIsOpenModalActivate(false);
    setIsOpenModalDeactivate(false);
  };

  const handleChangeActive = () => {
    if (!isActiveStatus) {
      if (publishIsDisabled) {
        setAlert(ActionTypes.SET_SAME_PAGE_ALERT, {
          text: t('licenceConfigurator.noActiveApplicationProcess.text'),
          variant: EAlertVariants.error,
        });
      } else {
        setIsOpenModalActivate(true);
      }
    } else {
      setIsOpenModalDeactivate(true);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[
          {
            label: t('module.administration.name'),
            route: ERouteLinks.Administration,
          },
          {
            label: t('licenceConfiguration.header'),
            route: ERouteLinks.LicenceConfigurationPage,
          },
          t('licenceConfiguration.detail.header'),
        ]}
      />

      <FormModal
        open={isOpenModalActivate}
        onClickClose={() => setIsOpenModalActivate(false)}
        onClickApprove={() => changeStatus()}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        titleLeft
        title={t('licenceConfigurator.activeLicenceDefinition.label')}
        text={t('licenceConfigurator.activeLicenceDefinition.text')}
      />
      <FormModal
        open={isOpenModalDeactivate}
        onClickClose={() => setIsOpenModalDeactivate(false)}
        onClickApprove={() => changeStatus()}
        approveButtonLabel="general.yes.label"
        cancelButtonLabel="general.no.label"
        titleLeft
        title={t('licenceConfigurator.deactivateLicenceDefinition.label')}
        text={t('licenceConfigurator.deactivateLicenceDefinition.text')}
      />

      {initLicenceValues && !isLoading && !publishStatusLoading ? (
        <>
          <Grid
            container
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              gap: 2,
            }}
          >
            <Grid>
              <StyledPageTitle variant="h4" sx={{ mb: 0 }}>
                {t('licenceConfiguration.detail.header')}
              </StyledPageTitle>
            </Grid>
            {licence && isActiveStatus !== undefined && !publishStatusLoading && (
              <Grid sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  id={`licence_configuration-edit-${licence.uniqueId}`}
                  label={t('licenceConfiguration.edit.button')}
                  variant={EButtonVariants.contained}
                  size={EButtonSizes.small}
                  onClick={() =>
                    navigate(
                      ERouteLinks.EditLicenceTemplatePage.replace(':id', licence.uniqueId),
                    )
                  }
                />
                <Button
                  id={`licence_configuration-${status}-${licence.uniqueId}`}
                  label={
                    isActiveStatus ? t('global.deactivate.label') : t('global.activate.label')
                  }
                  variant={EButtonVariants.contained}
                  styleVariant={isActiveStatus ? 'error' : 'success'}
                  size={EButtonSizes.small}
                  onClick={() => handleChangeActive()}
                />
              </Grid>
            )}
          </Grid>
          {samePageAlert && (
            <Grid sx={{ my: 1 }}>
              <Alert text={t(samePageAlert.text)} variant={samePageAlert.variant} />
            </Grid>
          )}
          {nextPageAlert && (
            <Grid size={{ xs: 12 }}>
              <Alert text={nextPageAlert.text} variant={nextPageAlert.variant} />
            </Grid>
          )}

          <DetailLicenceTemplatePage
            licence={initLicenceValues}
            licencePrices={licencePrices}
            isProcessingPricingLoading={isProcessingPricingLoading}
            weaponLicenceTemplateStatus={weaponLicenceTemplateStatus}
          />
        </>
      ) : (
        <Loader isLoading={true} />
      )}
    </>
  );
};

export { LicenceTemplatePage };
